import { Cycle } from "@qendo/trackables";
import { type DateArg, differenceInDays, endOfMonth, isBefore, isSameDay } from "date-fns";
import { router, Stack } from "expo-router";
import { useState } from "react";
import { StyleSheet, View } from "react-native";
import { CalendarList, type DateData } from "react-native-calendars";
import { Markings } from "react-native-calendars/src/calendar/day/marking";
import { Button } from "~/components/Button";
import { DayPickerItem } from "~/components/layout/calendar/DayPickerItem";
import { OnboardingSkip } from "~/components/layout/home/OnboardingSkip";
import { Page } from "~/components/layout/Page";
import { Subtitle, Title } from "~/components/layout/text";
import { Authorize } from "~/components/permission/Authorize";
import { Nothing } from "~/components/permission/Nothing";

import { Space } from "~/constants/sizing";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useJournal } from "~/providers/JournalProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { filled } from "~/util";
import { datesBetween, formatDateString } from "~/util/dates";
import { setHeightOnLayout } from "~/util/layout";

export default function LastPeriod() {
    const { border } = useTheme();
    const { saving, setSaving } = useFeedback();
    const { refreshCycle } = useProfile();
    const { storePeriod } = useJournal();
    const [started, setStarted] = useState<DateArg<Date> | null>(null);
    const [ended, setEnded] = useState<DateArg<Date> | null>(null);
    const today = formatDateString(new Date());
    const end_of_month = formatDateString(endOfMonth(today));

    const [height, setHeight] = useState(200);
    const [headerHeight, setHeaderHeight] = useState(100);
    const [footerHeight, setFooterHeight] = useState(100);

    const onContainerLayout = setHeightOnLayout(setHeight);
    const onHeaderLayout = setHeightOnLayout(setHeaderHeight);
    const onFooterLayout = setHeightOnLayout(setFooterHeight);

    const range = started ? datesBetween(started, ended ?? end_of_month) : [];

    const marked_dates = started
        ? Object.fromEntries(
              range.map((date) => [
                  formatDateString(date),
                  {
                      selected: isSameDay(started, date) || Boolean(ended && isSameDay(ended, date)),
                      marked: true,
                      type: Markings.PERIOD,
                  },
              ]),
          )
        : {};

    const body_height = Math.floor(height - headerHeight - footerHeight);
    const can_save = started && !saving;

    const handleDayPress = (date: DateData) => {
        const selected = date.dateString as DateArg<Date>;

        if (!started) {
            setStarted(selected);
        } else if (selected === started) {
            if (ended) {
                setStarted(ended);
                setEnded(null);
            } else {
                setStarted(null);
            }
        } else if (selected === ended) {
            setEnded(null);
        } else {
            if (isBefore(selected, started)) {
                ended || setEnded(started);
                setStarted(selected);
            } else if (ended && isBefore(selected, ended)) {
                if (differenceInDays(selected, started) >= differenceInDays(ended, selected)) {
                    setEnded(selected);
                } else {
                    setStarted(selected);
                }
            } else {
                setEnded(selected);
            }
        }
    };

    const handleSave = async () => {
        if (!can_save) {
            return;
        }

        setSaving(true);

        storePeriod({ recorded_at: started, status: Cycle.started })
            .then(async () => {
                if (ended) {
                    await storePeriod({ recorded_at: ended, status: Cycle.ended });
                }
                await refreshCycle();

                router.replace("/on-boarding/period-prediction");
            })
            .finally(() => setSaving(false));
    };

    return (
        <>
            <Stack.Screen
                options={{
                    headerLeft: () => <Nothing />,
                    headerRight: () => <OnboardingSkip next="/on-boarding/period-prediction" />,
                }}
            />
            <Authorize ability="update" section="cycle">
                <Page spacer scrollable={false} padding={0} gap={Space.sm}>
                    <View style={styles.container} onLayout={onContainerLayout}>
                        <View style={styles.heading} onLayout={onHeaderLayout}>
                            <Title>When was your most recent period?</Title>
                            <Subtitle>If you are not sure, pick an approximate date.</Subtitle>
                        </View>
                        <View style={{ maxHeight: body_height }}>
                            <CalendarList
                                futureScrollRange={0}
                                markedDates={marked_dates}
                                scrollEnabled
                                dayComponent={DayPickerItem}
                                onDayPress={handleDayPress}
                                maxDate={today}
                            />
                        </View>
                        <View style={[styles.buttons, border.primary]} onLayout={onFooterLayout}>
                            <Button
                                variant="primary"
                                size="lg"
                                title="Continue"
                                disabled={!can_save}
                                loading={filled(saving)}
                                onPress={handleSave}
                            />
                            <Button
                                variant="secondary"
                                size="lg"
                                title="I don't remember"
                                disabled={filled(saving)}
                                replace
                                href="/on-boarding/period-prediction"
                            />
                        </View>
                    </View>
                </Page>
            </Authorize>
        </>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    heading: {
        gap: Space.md,
        padding: Space.xl,
        paddingTop: Space.sm,
    },
    buttons: {
        flexShrink: 0,
        borderTopWidth: 1,
        alignItems: "stretch",
        gap: Space.md,
        padding: Space.xl,
        paddingTop: Space.lg,
    },
});
